/**
 * Quantity control for the collection list view.
 *
 * A card there has two ways to add — the ordinary minimum cut and the wholesale
 * quantity — and both need to lead to the same place: once the fabric is in the
 * cart, the card shows how many metres are in it and lets that be corrected on the
 * spot. The theme's own card stepper is tied to the grid's add-to-cart form and its
 * two-step reveal; this one edits the cart line directly instead, so either button
 * ends in the same single control.
 *
 * Owns the whole action column's state: with the fabric in the cart it shows the
 * stepper and "go to cart" and hides both add buttons, and back again at zero.
 */
if (!customElements.get('bf-cart-qty')) {
  customElements.define(
    'bf-cart-qty',
    class BfCartQty extends HTMLElement {
      // Long enough to collect a burst of clicks on the stepper, short enough that
      // the cart is already right by the time anyone reads the number.
      static COMMIT_DELAY = 350;

      connectedCallback() {
        this.input = this.querySelector('input');
        this.card = this.closest('.product-card');
        this.variantId = String(this.dataset.variantId);
        this.lastQuantity = Number(this.input.value) || 0;
        this.commitTimer = null;

        this.addEventListener('click', this.onButtonClick);
        this.input.addEventListener('change', this.onInputChange);

        this.unsubscribe = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, ({ cart }) =>
          this.sync(cart)
        );
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
        clearTimeout(this.commitTimer);
      }

      /**
       * Steps off the field's own value rather than off the cart, so a burst of
       * clicks adds up instead of each one racing the previous request. Nothing is
       * disabled while a request is in flight — a stepper that stops responding to
       * the second click reads as broken.
       */
      onButtonClick = (event) => {
        const button = event.target.closest('button[name]');
        if (!button) return;
        event.preventDefault();

        const step = Number(this.dataset.step) || 1;
        const current = Number(this.input.value);
        this.request((Number.isFinite(current) ? current : this.lastQuantity) + (button.name === 'plus' ? step : -step));
      };

      /** An empty or unparseable field is a slip, not an instruction to empty the line. */
      onInputChange = () => {
        const typed = this.input.value.trim();
        if (typed === '' || !Number.isFinite(Number(typed))) {
          this.input.value = this.lastQuantity;
          return;
        }
        this.request(Number(typed));
      };

      /** Quantity in the cart for this variant, or 0. */
      quantityIn(cart) {
        const line = cart?.items?.find((item) => String(item.variant_id) === this.variantId);
        return line ? line.quantity : 0;
      }

      sync(cart) {
        // A pending edit is newer than whatever this cart snapshot says.
        if (this.commitTimer) return;
        const quantity = this.quantityIn(cart);
        this.lastQuantity = quantity;
        this.input.value = quantity;
        this.toggleColumn(quantity > 0);
      }

      toggleColumn(inCart) {
        this.classList.toggle('hidden', !inCart);
        this.card?.querySelectorAll('.product-card__list-actions .product-card__list-atc').forEach((button) => {
          button.classList.toggle('hidden', inCart);
        });
        this.card?.querySelectorAll('.product-card__list-actions .go-cart').forEach((link) => {
          link.classList.toggle('hidden', !inCart);
        });
      }

      /**
       * Shows the new quantity straight away and writes it once the clicking stops.
       * Below the product's minimum the line is removed rather than left at an
       * unbuyable length.
       */
      request(quantity) {
        const minimum = Number(this.dataset.min) || 1;
        const maximum = Number(this.dataset.max) || 0;

        let target = Math.floor(quantity);
        if (!Number.isFinite(target) || target < 0) target = 0;
        if (target > 0 && target < minimum) target = 0;
        if (maximum > 0 && target > maximum) target = maximum;

        this.input.value = target;
        this.classList.add('bf-cart-qty--busy');

        clearTimeout(this.commitTimer);
        this.commitTimer = setTimeout(() => {
          this.commitTimer = null;
          this.commit(target);
        }, BfCartQty.COMMIT_DELAY);
      }

      async commit(target) {
        try {
          const response = await fetch(`${FoxTheme.routes.cart_change_url}`, {
            ...FoxTheme.utils.fetchConfig(),
            body: JSON.stringify({ id: this.variantId, quantity: target }),
          });
          let cart = await response.json();

          // Asking for more than is in stock is refused, and the refusal comes back
          // as an error object rather than a cart. Reading it as one made the card
          // claim the fabric had left the cart while it was still sitting there at
          // the quantity the shop could actually supply — so re-read the real cart
          // and show that.
          if (!response.ok || !Array.isArray(cart.items)) {
            cart = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
          }

          this.lastQuantity = this.quantityIn(cart);
          this.input.value = this.lastQuantity;
          this.toggleColumn(this.lastQuantity > 0);
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
        } catch (error) {
          // Put the field back to what the cart last confirmed rather than leaving a
          // number on screen that was never saved.
          this.input.value = this.lastQuantity;
          console.log(error);
        } finally {
          this.classList.remove('bf-cart-qty--busy');
        }
      }
    }
  );
}
